import DoctorVisitForm from "../../components/forms/doctorVisitForm";
import DoctorVisitTable from "../../components/tables/doctorVisitTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { exportToCSV, doctorVisitCSVColumns } from "../../utils/exportUtils";
import { exportDoctorVisitReportPDF } from "../../utils/pdfUtils";

const DoctorVisits = () => {
  const { selectedChild } = useChild();
  const {
    items: visits, addItem, updateItem, deleteItem, loading, error,
  } = useFirestore("doctorVisits", selectedChild?.id);

  const handleAdd = async (visit) => {
    try {
      await addItem({ ...visit, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding doctor visit:", err);
    }
  };

  const handleCSV = () =>
    exportToCSV(
      visits,
      `doctor-visits-${selectedChild?.name || "export"}.csv`,
      doctorVisitCSVColumns
    );

  const handleReportPDF = () =>
    exportDoctorVisitReportPDF(
      visits,
      selectedChild?.name || "Child",
      selectedChild?.dateOfBirth || ""
    );

  // Upcoming follow-ups (not yet passed)
  const upcomingFollowUps = visits.filter(
    (v) => v.followUpDate && new Date(v.followUpDate) >= new Date()
  ).sort((a, b) => new Date(a.followUpDate) - new Date(b.followUpDate));

  return (
    <div className="card h-100" id="doctor-visits">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="card-title mb-0">🏥 Doctor Visits</h5>
          <div className="d-flex gap-1 flex-wrap">
            <button className="btn btn-sm btn-outline-secondary" onClick={handleCSV}>
              ⬇ CSV
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleReportPDF}>
              📋 Visit Report PDF
            </button>
          </div>
        </div>

        {/* Upcoming follow-up banner */}
        {upcomingFollowUps.length > 0 && (
          <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.85rem" }}>
            <strong>📅 Upcoming follow-up{upcomingFollowUps.length > 1 ? "s" : ""}:</strong>{" "}
            {upcomingFollowUps.slice(0, 2).map((v, i) => (
              <span key={v.id}>
                {i > 0 && " · "}
                {new Date(v.followUpDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                {v.practitioner ? ` (${v.practitioner})` : ""}
              </span>
            ))}
            {upcomingFollowUps.length > 2 && ` · +${upcomingFollowUps.length - 2} more`}
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        <DoctorVisitForm onAdd={handleAdd} />

        {loading ? (
          <LoadingState message="Loading visit history..." />
        ) : visits.length > 0 ? (
          <DoctorVisitTable data={visits} onUpdate={updateItem} onDelete={deleteItem} />
        ) : (
          <EmptyState
            icon="🏥"
            heading="No visits recorded yet"
            message="Log your first appointment and keep a clear picture of your child's health journey."
          />
        )}
      </div>
    </div>
  );
};

export default DoctorVisits;
